import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faPaperclip, faImage, faFile, faMicrophone, faVideo, faSmile, faUsersCog, faUsersSlash, faUserFriends, faUser, faPhone, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Popover, PopoverBody } from 'reactstrap';
import moment from 'moment';
import EmojiPicker from 'emoji-picker-react';
import { toast } from 'react-toastify';

const INITIAL_MESSAGE_WINDOW = 50;
const LOAD_MORE_BATCH = 50;
const MMS_MIN_HEIGHT = 200;

const MediaSkeleton = ({ height = MMS_MIN_HEIGHT, mediaType = 'image' }) => (
  <div
    className="mms-media-skeleton relative w-full overflow-hidden rounded-lg"
    style={{ height, minHeight: height }}
    aria-hidden="true"
  >
    <div className="absolute inset-0 flex items-center justify-center">
      <FontAwesomeIcon
        icon={mediaType === 'video' ? faVideo : faImage}
        className="text-gray-400"
        style={{ fontSize: '28px', opacity: 0.35 }}
      />
    </div>
  </div>
);

const Messages = ({
  chat, 
  onBack, 
  isMobile, 
  webSocketData,
  getDisplayName,
  username,
  password,
  cloudId
}) => {
  const [message, setMessage] = useState('');
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  const userHasScrolledRef = useRef(false);
  const lastScrollHeightRef = useRef(0);
  const lastScrollTopRef = useRef(0);
  const isAwaitingMediaPinRef = useRef(false);
  const mediaPinTimeoutRef = useRef(null);
  const messagesListRef = useRef(null);
  const [isMultiLine, setIsMultiLine] = useState(false);
  const [loading, setLoading] = useState(true);
  const [messagesReady, setMessagesReady] = useState(false);
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [loadedMediaKeys, setLoadedMediaKeys] = useState(() => new Set());
  
  // Reset messages when chat changes
  const [messages, setMessages] = useState([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverTimeoutRef = useRef(null);
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const sentBlobUrlsRef = useRef(new Set());
  const pendingOutboundRef = useRef(null);
  const pendingScrollRestoreRef = useRef(null);
  const isLoadingOlderRef = useRef(false);
  const visibleStartIndexRef = useRef(0);
  const [fileType, setFileType] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);

  const hasMessageContent = (msg) =>
    Boolean(msg?.remotepath) ||
    Boolean(msg?.text && msg.text.trim() && msg.text !== ' ');

  const getMediaKey = (msg) => `${msg.id}-${msg.remotepath || ''}`;

  const isMediaLayoutReady = (msg) => loadedMediaKeys.has(getMediaKey(msg));

  const countPendingVisibleMedia = () => {
    const list = messagesListRef.current;
    if (!list) return 0;

    let pending = 0;
    list.querySelectorAll('img[src]').forEach(img => {
      if (!img.complete) pending += 1;
    });
    list.querySelectorAll('video[src]').forEach(video => {
      if (video.readyState < 1) pending += 1;
    });
    return pending;
  };

  const buildWebSocketMessage = (data) => ({
    id: data.RemoteSMSID,
    text: data.Message,
    direction: data.Direction === 'outbound' ? 'outbound' : 'inbound',
    from: data.FromUser || data.FromUID || '',
    from_num: username.split('@')[0] !== data.FromUser ? data.FromUser : '' || '',
    time: moment.utc(data.CreatedTime).local().calendar(null, {
      sameDay: 'h:mm A',
      nextDay: 'ddd h:mm A',
      nextWeek: 'ddd h:mm A',
      lastDay: 'ddd h:mm A',
      lastWeek: 'ddd h:mm A',
      sameElse: 'MM/DD h:mm A'
    }),
    timestamp: moment.utc(data.CreatedTime).valueOf(),
    remotepath: data.remotepath || data.RemotePath || data.remote_path,
    media_type: data.media_type || data.MediaType
  });

  const findOptimisticOutboundIndex = (messageList, wsTimestamp) => {
    const pending = pendingOutboundRef.current;
    if (pending) {
      const pendingIndex = messageList.findIndex(
        msg => msg.id === pending.tempId || msg.id === pending.serverId
      );
      if (pendingIndex !== -1) return pendingIndex;
    }

    for (let i = messageList.length - 1; i >= 0; i--) {
      const msg = messageList[i];
      if (msg.direction !== 'outbound') continue;

      const isTempId = typeof msg.id === 'number' && msg.id > 1e12;
      const isRecent = Math.abs(msg.timestamp - wsTimestamp) < 60000;
      if (isTempId && isRecent) return i;
    }

    return -1;
  };

  useEffect(() => {
    if (!chat) return;

    let cancelled = false;
    setLoading(true);
    setMessagesReady(false);
    setMessages([]);
    setVisibleStartIndex(0);
    setLoadingOlder(false);
    setLoadedMediaKeys(new Set());
    isAwaitingMediaPinRef.current = false;
    if (mediaPinTimeoutRef.current) {
      clearTimeout(mediaPinTimeoutRef.current);
      mediaPinTimeoutRef.current = null;
    }
    shouldStickToBottomRef.current = true;
    userHasScrolledRef.current = false;
    lastScrollHeightRef.current = 0;
    lastScrollTopRef.current = 0;

    const payLoad = {
      username: username,
      password: password,
      cloud_id: cloudId,
      session_id: chat.id
    }
    if (chat.sharedUsers?.length > 0) {
      payLoad.shared_message_flag = true;
      payLoad.shared_did = chat.sharedDid;
      payLoad.secondary_user = chat.sharedUsers;
      payLoad.primary_user = chat.primaryUser;
    }
    axios.post(`${process.env.REACT_APP_MESSAGE_API_URL}/getMessages`, payLoad)
    .then(response => {
        if (cancelled) return;
        const myMessages = [];
        response.data.map(message => {
          myMessages.push({
            id: message.id, 
            text: message.text, 
            direction: (username && message.from_uid.toLowerCase() === username.toLowerCase()) || (chat.primaryUser && message.from_uid.toLowerCase() === chat.primaryUser.toLowerCase()) ? "outbound" : "inbound", 
            from: message.from_uid,
            from_num: message.from_num,
            time: moment.utc(message.timestamp).local().calendar(null, {
              sameDay: 'h:mm A',
              nextDay: 'ddd h:mm A',
              nextWeek: 'ddd h:mm A',
              lastDay: 'ddd h:mm A',
              lastWeek: 'ddd h:mm A',
              sameElse: 'MM/DD h:mm A'
            }),
            timestamp: message.timestamp,
            remotepath: message.remotepath,
            media_type: message.media_type
          })
        })
        myMessages.reverse();
        setMessages(myMessages);
        setVisibleStartIndex(Math.max(0, myMessages.length - INITIAL_MESSAGE_WINDOW));
        setMessagesReady(true);
    })
    .catch(error => {
      if (cancelled) return;
      console.error('Error fetching sessions:', error);
      setMessagesReady(true);
    });

    return () => {
      cancelled = true;
      pendingOutboundRef.current = null;
      if (mediaPinTimeoutRef.current) {
        clearTimeout(mediaPinTimeoutRef.current);
        mediaPinTimeoutRef.current = null;
      }
      sentBlobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      sentBlobUrlsRef.current.clear();
    };
  }, [chat?.id]); // Only run when chat id changes

  // Handle new WebSocket messages
  useEffect(() => {
    if (!webSocketData || !chat || webSocketData.SessionID !== chat.id) return;

    const wsMessage = buildWebSocketMessage(webSocketData);

    setMessages(prevMessages => {
      if (prevMessages.some(msg => msg.id === wsMessage.id)) {
        return prevMessages;
      }

      if (wsMessage.direction === 'outbound') {
        const mergeIndex = findOptimisticOutboundIndex(prevMessages, wsMessage.timestamp);
        if (mergeIndex !== -1) {
          const existing = prevMessages[mergeIndex];
          const merged = {
            ...existing,
            id: wsMessage.id,
            text: hasMessageContent({ text: wsMessage.text }) ? wsMessage.text : existing.text,
            remotepath: wsMessage.remotepath || existing.remotepath,
            media_type: wsMessage.media_type || existing.media_type,
            time: wsMessage.time,
            timestamp: wsMessage.timestamp
          };

          if (existing.remotepath?.startsWith('blob:') && wsMessage.remotepath && !wsMessage.remotepath.startsWith('blob:')) {
            URL.revokeObjectURL(existing.remotepath);
            sentBlobUrlsRef.current.delete(existing.remotepath);
          }

          pendingOutboundRef.current = null;
          const updated = [...prevMessages];
          updated[mergeIndex] = merged;
          return updated;
        }

        if (!hasMessageContent(wsMessage)) {
          return prevMessages;
        }
      }

      if (!hasMessageContent(wsMessage)) {
        return prevMessages;
      }

      return [...prevMessages, wsMessage];
    });
  }, [webSocketData, chat?.id, username]);

  const jumpToBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  };

  const stickToBottomIfNeeded = () => {
    if (userHasScrolledRef.current || !shouldStickToBottomRef.current) return;
    jumpToBottom();
  };

  const markMediaLoaded = (msg) => {
    const key = getMediaKey(msg);
    setLoadedMediaKeys(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const finishInitialMediaPinIfReady = () => {
    if (!isAwaitingMediaPinRef.current || userHasScrolledRef.current) return;

    jumpToBottom();

    if (countPendingVisibleMedia() === 0) {
      isAwaitingMediaPinRef.current = false;
      if (mediaPinTimeoutRef.current) {
        clearTimeout(mediaPinTimeoutRef.current);
        mediaPinTimeoutRef.current = null;
      }
      requestAnimationFrame(jumpToBottom);
    }
  };

  const startInitialMediaPin = () => {
    isAwaitingMediaPinRef.current = true;
    shouldStickToBottomRef.current = true;
    userHasScrolledRef.current = false;

    jumpToBottom();
    requestAnimationFrame(() => {
      jumpToBottom();
      finishInitialMediaPinIfReady();
    });

    if (mediaPinTimeoutRef.current) {
      clearTimeout(mediaPinTimeoutRef.current);
    }
    mediaPinTimeoutRef.current = setTimeout(() => {
      isAwaitingMediaPinRef.current = false;
      if (!userHasScrolledRef.current) {
        jumpToBottom();
      }
    }, 15000);
  };

  const loadOlderMessages = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || isLoadingOlderRef.current || visibleStartIndexRef.current === 0) return;

    userHasScrolledRef.current = true;
    shouldStickToBottomRef.current = false;
    isAwaitingMediaPinRef.current = false;
    isLoadingOlderRef.current = true;
    setLoadingOlder(true);
    pendingScrollRestoreRef.current = {
      scrollTop: container.scrollTop,
      scrollHeight: container.scrollHeight
    };
    setVisibleStartIndex(prev => Math.max(0, prev - LOAD_MORE_BATCH));
  }, []);

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const scrollHeightGrew = scrollHeight > lastScrollHeightRef.current + 1;
    const scrolledUp = scrollTop < lastScrollTopRef.current - 2;

    if (scrolledUp) {
      userHasScrolledRef.current = true;
      shouldStickToBottomRef.current = false;
      isAwaitingMediaPinRef.current = false;
    } else if (distanceFromBottom < 20) {
      userHasScrolledRef.current = false;
      shouldStickToBottomRef.current = true;
    } else {
      shouldStickToBottomRef.current = distanceFromBottom < 120;
    }

    if (
      !userHasScrolledRef.current &&
      scrollHeightGrew &&
      (isAwaitingMediaPinRef.current || shouldStickToBottomRef.current)
    ) {
      jumpToBottom();
    }

    lastScrollHeightRef.current = scrollHeight;
    lastScrollTopRef.current = scrollTop;

    if (container.scrollTop < 80 && visibleStartIndexRef.current > 0 && !isLoadingOlderRef.current) {
      loadOlderMessages();
    }
  };

  const handleMediaLoad = (msg) => {
    markMediaLoaded(msg);

    if (isAwaitingMediaPinRef.current && !userHasScrolledRef.current) {
      requestAnimationFrame(() => {
        jumpToBottom();
        requestAnimationFrame(finishInitialMediaPinIfReady);
      });
      return;
    }

    stickToBottomIfNeeded();
  };

  const handleMediaRef = (msg, element) => {
    if (!element) return;

    if (element.tagName === 'IMG' && element.complete) {
      handleMediaLoad(msg);
    } else if (element.tagName === 'VIDEO' && element.readyState >= 1) {
      handleMediaLoad(msg);
    }
  };

  useEffect(() => {
    visibleStartIndexRef.current = visibleStartIndex;
  }, [visibleStartIndex]);

  useLayoutEffect(() => {
    const pending = pendingScrollRestoreRef.current;
    if (!pending) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    container.scrollTop = pending.scrollTop + (container.scrollHeight - pending.scrollHeight);
    pendingScrollRestoreRef.current = null;
    isLoadingOlderRef.current = false;
    setLoadingOlder(false);
  }, [visibleStartIndex]);

  useLayoutEffect(() => {
    if (!messagesReady || !loading) return;

    jumpToBottom();
    const frame = requestAnimationFrame(() => {
      jumpToBottom();
      lastScrollHeightRef.current = messagesContainerRef.current?.scrollHeight || 0;
      lastScrollTopRef.current = messagesContainerRef.current?.scrollTop || 0;
      setLoading(false);
    });

    return () => cancelAnimationFrame(frame);
  }, [messagesReady, loading]);

  useLayoutEffect(() => {
    if (!messagesReady || loading) return;

    startInitialMediaPin();
  }, [messagesReady, loading]);

  useLayoutEffect(() => {
    if (!messagesReady || loading) return;
    stickToBottomIfNeeded();
  }, [messages, messagesReady, loading]);

  useEffect(() => {
    const list = messagesListRef.current;
    if (!list || !messagesReady || loading) return;

    const keepPinned = () => {
      if (isAwaitingMediaPinRef.current && !userHasScrolledRef.current) {
        jumpToBottom();
        return;
      }
      stickToBottomIfNeeded();
    };

    const observer = new ResizeObserver(keepPinned);
    observer.observe(list);

    return () => observer.disconnect();
  }, [messagesReady, loading, visibleStartIndex]);

  const getFrom = (message) => {
   if (message.from) {
      return message.from;
    } else if (message.from_num) {
      return message.from_num;
    }
    return "";
  }

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 120);
      textarea.style.height = `${newHeight}px`;
      setIsMultiLine(newHeight > 48);
    }
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    setCursorPosition(e.target.selectionStart);
    adjustTextareaHeight();
  };

  const handleEmojiClick = (emojiObject) => {
    const emoji = emojiObject.emoji;
    const textarea = textareaRef.current;
    // Always get the latest cursor position from the DOM
    const selectionStart = textarea.selectionStart;
    const text = textarea.value;
    const newText = text.slice(0, selectionStart) + emoji + text.slice(selectionStart);
    setMessage(newText);

    // Update cursor position after emoji insertion
    const newCursorPosition = selectionStart + emoji.length;
    setCursorPosition(newCursorPosition);

    // Set cursor position after the inserted emoji
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  const handleTextareaClick = (e) => {
    const newCursorPosition = e.target.selectionStart;
    setCursorPosition(newCursorPosition);
  };

  const handleTextareaKeyUp = (e) => {
    const newCursorPosition = e.target.selectionStart;
    setCursorPosition(newCursorPosition);
  };

  const handleTextareaKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
    // Update cursor position on keydown as well
    const newCursorPosition = e.target.selectionStart;
    setCursorPosition(newCursorPosition);
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (filePreview && !sentBlobUrlsRef.current.has(filePreview)) {
        URL.revokeObjectURL(filePreview);
      }
      setSelectedFile(file);
      setFileType(file.type.split('/')[0]); // 'image', 'video', 'audio', or 'application'
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setFilePreview(previewUrl);
      setShowMediaOptions(false);
      
      // Clear the file input
      event.target.value = '';
    }
  };

  // Clear composer file selection. Do not revoke blob URLs that a sent message is still showing.
  const clearFilePreview = (revokePreview = true) => {
    if (revokePreview && filePreview && !sentBlobUrlsRef.current.has(filePreview)) {
      URL.revokeObjectURL(filePreview);
    }
    setSelectedFile(null);
    setFilePreview(null);
    setFileType(null);
  };

  const findSmsTo = (phone, name) => {
    if (phone) {
      return phone;
    }
    if (name.startsWith('[')) {
      try {
        // Parse the JSON array string
        const members = JSON.parse(name);
        // Map each member to their display name and join with commas
        return members
          .map(member => {
            // Handle multiple @ symbols - keep only up to the second @
            let memberName = member.split('@')[0];
           
            const myChatName = memberName;
            return myChatName;
          })
          .join(',');
      } catch (error) {
        // If JSON parsing fails, return the original name
        console.error('Error parsing group members:', error);
        return name;
      }
    } else {
      return name.split('@')[0];
    }
  }

  const handleSendMessage = () => {
    if ((message.trim() || selectedFile) && !sending) {
      setSending(true);
      
      // Store the current message and file in case of error
      const messageToSend = message;
      const fileToSend = selectedFile;
      const filePreviewToRestore = filePreview;
      const fileTypeToRestore = fileType;
      
      const formData = new FormData();
      formData.append('from', username);
      formData.append('password', password);
      const smsTo = findSmsTo(chat.phone, chat.name)
      formData.append('sms_to', smsTo);
      formData.append('cloud_id', cloudId);
      formData.append('media_attached', selectedFile ? 'true' : 'false');
      formData.append('sms_body', message);
      if (chat.sharedUsers?.length > 0) {
        formData.append('shared_message_flag', true);
        formData.append('did', chat.sharedDid);
        formData.append('primary_user', chat.primaryUser);
      }

      if (selectedFile) {
        formData.append('files', selectedFile);
      }
      
      // Create new message object immediately with preview
      const now = new Date();
      const newMessage = {
        id: Date.now(), // Temporary ID until API response
        text: message,
        direction: "outbound",
        from_num: chat.myPhone,
        from: username,
        time: moment.utc(now).local().calendar(null, {
          sameDay: 'h:mm A',
          nextDay: 'ddd h:mm A',
          nextWeek: 'ddd h:mm A',
          lastDay: 'ddd h:mm A',
          lastWeek: 'ddd h:mm A',
          sameElse: 'MM/DD h:mm A'
        }),
        timestamp: now.getTime(),
        // Keep the local blob on the outbound bubble until a server URL exists
        ...(selectedFile && {
          remotepath: filePreview,
          media_type: selectedFile.type
        })
      };

      if (filePreview) {
        sentBlobUrlsRef.current.add(filePreview);
      }

      pendingOutboundRef.current = {
        tempId: newMessage.id,
        serverId: null,
        timestamp: now.getTime()
      };
      
      // Add message to UI immediately and keep the view on the latest message
      shouldStickToBottomRef.current = true;
      userHasScrolledRef.current = false;
      setMessages(prevMessages => [...prevMessages, newMessage]);
      
      // Clear input fields optimistically
      setMessage('');
      setIsMultiLine(false);
      
      if (textareaRef.current) {
        textareaRef.current.style.height = '48px';
      }
      
      // Clear the composer only. Keep the blob URL so the sent message can render it.
      clearFilePreview(false);
      
      // Make API call
      axios.post(`${process.env.REACT_APP_MESSAGE_API_URL}/sendMessage`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => {
        const serverId = response.data?.RemoteSMSID || response.data?.id;
        const serverMedia = response.data?.remotepath || response.data?.remote_path;
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            msg.id === newMessage.id 
              ? {
                  ...msg,
                  id: serverId || msg.id,
                  ...(serverMedia ? { remotepath: serverMedia, media_type: response.data?.media_type || msg.media_type } : {})
                }
              : msg
          )
        );
        if (pendingOutboundRef.current?.tempId === newMessage.id) {
          pendingOutboundRef.current = {
            ...pendingOutboundRef.current,
            serverId: serverId || pendingOutboundRef.current.serverId
          };
        }
        if (serverMedia && filePreviewToRestore) {
          URL.revokeObjectURL(filePreviewToRestore);
          sentBlobUrlsRef.current.delete(filePreviewToRestore);
          pendingOutboundRef.current = null;
        }
      })
      .catch(error => {
        console.error("Error sending message:", error);
        
        // Show error toast
        toast.error('Failed to send message. Please try again.');
        
        // Remove the failed message from UI
        setMessages(prevMessages => 
          prevMessages.filter(msg => msg.id !== newMessage.id)
        );

        pendingOutboundRef.current = null;

        if (filePreviewToRestore) {
          sentBlobUrlsRef.current.delete(filePreviewToRestore);
        }
        
        // Restore the message text in input field
        setMessage(messageToSend);
        
        // Restore file preview if there was one
        if (fileToSend) {
          setSelectedFile(fileToSend);
          setFilePreview(filePreviewToRestore);
          setFileType(fileTypeToRestore);
        }
        
        // Restore textarea height if it was multiline
        if (textareaRef.current && messageToSend.includes('\n')) {
          setIsMultiLine(true);
          // Recalculate height based on content
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
        }
      })
      .finally(() => {
        setSending(false);
      });
    }
  };

  const getMessageStyle = (message, index, messages) => {
    const prevMessage = index > 0 ? messages[index - 1] : null;
    const nextMessage = index < messages.length - 1 ? messages[index + 1] : null;
    const isOutbound = message.direction === 'outbound';

    // Check if message is part of a sequence
    const isPartOfSequence = (msg1, msg2) => {
      if (!msg1 || !msg2 || msg1.direction !== msg2.direction || 
          (msg1.direction === 'inbound' && msg1.from !== msg2.from)) return false;
      
      // Check if messages are from different numbers
      const msg1From = msg1.from_num || getFrom(msg1);
      const msg2From = msg2.from_num || getFrom(msg2);
      if (msg1From !== msg2From) return false;

      const time1 = new Date(msg1.timestamp || msg1.time);
      const time2 = new Date(msg2.timestamp || msg2.time);
      return (Math.abs(time2 - time1) / 1000 / 60) < 1;
    };

    const isFirstInSequence = !isPartOfSequence(prevMessage, message);
    const isLastInSequence = !isPartOfSequence(message, nextMessage);

    if (isFirstInSequence && isLastInSequence) {
      // Single message
      return 'rounded-2xl';
    }

    if (isFirstInSequence) {
      // First message in sequence
      return isOutbound ? 'rounded-2xl rounded-br-none rounded-bl-2xl' : 'rounded-2xl rounded-bl-none rounded-br-2xl';
    }

    if (isLastInSequence) {
      // Last message in sequence
      return isOutbound ? 'rounded-2xl rounded-tr-none' : 'rounded-2xl rounded-tl-none';
    }

    // Middle message in sequence
    return isOutbound ? 'rounded-l-2xl rounded-r-none' : 'rounded-r-2xl rounded-l-none';
  };

  const getAvatarColors = (name) => {
    // Predefined array of distinct colors
    const colors = [
      { bg: 'FFE766', text: '806B00' }, // Yellow
      { bg: '90EE90', text: '006400' }, // Light green
      { bg: 'ADD8E6', text: '00008B' }, // Light blue
      { bg: 'FFB6C1', text: '8B0000' }, // Light pink
      { bg: 'DDA0DD', text: '4B0082' }, // Plum
      { bg: 'F0E68C', text: '8B8000' }, // Khaki
      { bg: 'E0FFFF', text: '008B8B' }, // Light cyan
      { bg: 'D8BFD8', text: '4B0082' }, // Thistle
    ];

    // Generate a consistent index for each name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Use the hash to select a color pair
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  // Function to get group members list
  const getGroupMembers = (chatName) => {
    if (chatName.startsWith('[')) {
      try {
        const members = JSON.parse(chatName);
        return members.map(member => ({
          id: member,
          name: getDisplayName(member)
        }));
      } catch (error) {
        console.error('Error parsing group members:', error);
        return [];
      }
    }
    return [];
  };

  // Handle mouse enter for desktop
  const handleMouseEnter = () => {
    if (!isMobile && chat.name.startsWith('[')) {
      if (popoverTimeoutRef.current) {
        clearTimeout(popoverTimeoutRef.current);
      }
      setPopoverOpen(true);
    }
  };

  // Handle mouse leave for desktop
  const handleMouseLeave = () => {
    if (!isMobile) {
      popoverTimeoutRef.current = setTimeout(() => {
        setPopoverOpen(false);
      }, 300); // Small delay to prevent flickering
    }
  };

  // Handle click for mobile
  const handleClick = (e) => {
    if (isMobile && chat.name.startsWith('[')) {
      e.stopPropagation();
      setPopoverOpen(!popoverOpen);
    }
  };

  // Add isNumeric and getAvatarDisplayName helpers
  const isNumeric = (str) => {
    return /^\d+$/.test(str);
  };

  // Function to convert URLs in text to clickable links
  const convertUrlsToLinks = (text, isOutbound) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a 
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className={`underline ${isOutbound ? 'text-white' : 'text-blue-600'}`}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const getAvatarDisplayName = (name) => {
    const displayName = getDisplayName(name);
    return isNumeric(displayName) ? '#' : displayName;
  };

  // Render avatar based on group status
  const renderAvatar = () => {
    const isGroup = chat.name.startsWith('[');
    
    if (isGroup) {
      return (
        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-200">
          <FontAwesomeIcon 
            icon={faUsers}
            className="text-black"
            style={{ fontSize: "20px" }}
          />
        </div>
      );
    }
    
    const avatarName = getAvatarDisplayName(chat.name);
    if (avatarName === '#') {
      return (
        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-200">
          <FontAwesomeIcon 
            icon={faUser}
            className="text-black"
            style={{ fontSize: "20px" }}
          />
        </div>
      );
    }
    
    return (
      <img
        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(avatarName)}`}
        alt={avatarName}
        className="w-10 h-10 rounded-full object-cover"
      />
    );
  };

  const handleMediaClick = (msg) => {
    setPreviewMedia(msg);
    setShowPreview(true);
  };

  const handleDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const MediaPreviewModal = ({ media, isOpen, onClose }) => {
    if (!isOpen || !media) return null;

    return (
      <div 
        className="fixed inset-0 bg-black z-50 flex items-center justify-center"
        onClick={onClose}
      >
        <div className="relative w-full h-full flex items-center justify-center">
          {media.media_type?.startsWith('image/') ? (
            <div className="relative w-full h-full flex items-center justify-center">
              <button 
                onClick={onClose}
                className="absolute top-4 right-4 text-white hover:text-gray-300 z-50 bg-black bg-opacity-50 rounded-full p-1.5 backdrop-blur-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <img 
                src={media.remotepath} 
                alt="Media Preview" 
                className="max-w-full max-h-screen w-full h-full object-contain"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(media.remotepath, `image.${media.media_type.split('/')[1]}`);
                }}
                className="absolute bottom-4 right-4 bg-black bg-opacity-50 backdrop-blur-sm text-white rounded-full p-2 hover:bg-opacity-75 z-50 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="relative w-full h-full flex items-center justify-center bg-black">
              <button 
                onClick={onClose}
                className="absolute top-4 right-4 text-white hover:text-gray-300 z-50 bg-black bg-opacity-50 rounded-full p-1.5 backdrop-blur-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <video 
                src={media.remotepath} 
                controls 
                autoPlay
                className="max-w-full max-h-screen w-auto h-auto object-contain"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const getPhoneNumber = (name) => {
    // If it's a group chat, return null
    if (name.startsWith('[')) return null;
    
    // Extract number from name (remove any text after @)
    const number = name.split('@')[0];
    return number;
  };

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <style>{`
        .mms-media-skeleton {
          background-color: #e5e7eb;
        }
        .mms-media-skeleton::after {
          content: '';
          position: absolute;
          inset: 0;
          transform: translateX(-100%);
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.55) 50%,
            rgba(255, 255, 255, 0) 100%
          );
          animation: mmsShimmerWave 1.4s ease-in-out infinite;
        }
        @keyframes mmsShimmerWave {
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
      {/* Header */}
      <div className="flex-shrink-0 flex items-center p-4 border-b border-gray-200 bg-white shadow-sm z-10">
        {isMobile && (
          <button
            onClick={onBack}
            className="mr-4 text-gray-600 hover:text-gray-800"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        
        <div className="relative">
          {renderAvatar()}
        </div>

        <div 
          id="chat-header"
          className="ml-3 flex-1 relative"
          onClick={(e) => chat.name.startsWith('[') && handleClick(e)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <h2 className={`font-semibold text-xl text-gray-800 mb-0 max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap
                         ${chat.name.startsWith('[') ? 'hover:text-blue-600 cursor-pointer' : ''}`}>
            {getDisplayName(chat.name)}
          </h2>
          <p className="text-sm text-gray-500 mb-0 max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">{chat.name.replaceAll('[', '').replaceAll(']', '').replaceAll ('"', '')}</p>

          {chat.name.startsWith('[') && (
            <Popover
              placement="bottom-start"
              isOpen={popoverOpen}
              target="chat-header"
              trigger={isMobile ? 'click' : 'hover'}
              delay={{ show: 0, hide: 300 }}
              style={{
                minWidth: '250px'
              }}
            >
              <PopoverBody className="p-0">
                <div className="py-1">
                  <div className="px-3 py-2 text-sm font-medium text-gray-700 border-b border-gray-200">
                    Group Members
                  </div>
                  {getGroupMembers(chat.name).map((member, index) => (
                    <div 
                      key={index}
                      className="px-3 py-2 hover:bg-gray-50"
                    >
                      <div className="font-medium text-gray-800">{member.name}</div>
                      <div className="text-sm text-gray-500">{member.id}</div>
                    </div>
                  ))}
                </div>
              </PopoverBody>
            </Popover>
          )}
        </div>
        {!chat.isGroup && getPhoneNumber(chat.name) && (
          <a 
            href={`sip:${getPhoneNumber(chat.name)}?dialAction=autoCall`}
            className="text-gray-600 hover:text-gray-800 p-2"
          >
            <FontAwesomeIcon icon={faPhone} className="w-5 h-5" />
          </a>
        )}
      </div>

      {/* Messages Area */}
      <div className="relative flex-1 min-h-0 bg-gray-50">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-50">
            <svg className="w-8 h-8 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}
        <div
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          className={`absolute inset-0 overflow-y-auto p-4 ${loading ? 'invisible' : ''}`}
          style={{ WebkitOverflowScrolling: 'touch', overflowAnchor: 'none' }}
        >
        <div ref={messagesListRef} className="space-y-4">
          {visibleStartIndex > 0 && (
            <div className="flex justify-center py-2">
              <span className="text-xs text-gray-500">
                {loadingOlder ? 'Loading earlier messages...' : 'Scroll up for earlier messages'}
              </span>
            </div>
          )}
          {messages.slice(visibleStartIndex).map((msg, index) => {
            if (!hasMessageContent(msg)) return null;

            const globalIndex = visibleStartIndex + index;
            const prevMessage = globalIndex > 0 ? messages[globalIndex - 1] : null;
            const isPartOfSequence = (msg1, msg2) => {
              if (!msg1 || !msg2 || msg1.direction !== msg2.direction || 
                  (msg1.direction === 'inbound' && msg1.from !== msg2.from)) return false;
              
              // Check if messages are from different numbers
              const msg1From = msg1.from_num || getFrom(msg1);
              const msg2From = msg2.from_num || getFrom(msg2);
              if (msg1From !== msg2From) return false;

              const time1 = new Date(msg1.timestamp || msg1.time);
              const time2 = new Date(msg2.timestamp || msg2.time);
              return (Math.abs(time2 - time1) / 1000 / 60) < 1;
            };
            const shouldShowTime = !isPartOfSequence(prevMessage, msg);
            const shouldShowAvatar = msg.direction === 'inbound' && !isPartOfSequence(prevMessage, msg);

            // Check if we need to show a date separator
            const showDateSeparator = prevMessage && 
              !moment.utc(msg.timestamp).local().isSame(moment.utc(prevMessage.timestamp).local(), 'day');

            return (
              <div className="mt-2" key={msg.id ?? globalIndex}>
                {/* Date Separator */}
                {showDateSeparator && (
                  <div className="flex items-center my-4">
                    <div className="flex-grow border-t border-gray-200"></div>
                    <span className="px-4 text-sm text-gray-500">
                      {moment.utc(msg.timestamp).local().calendar(null, {
                        sameDay: '[Today]',
                        nextDay: '[Tomorrow]',
                        nextWeek: 'dddd',
                        lastDay: '[Yesterday]',
                        lastWeek: 'dddd',
                        sameElse: 'DD MMMM'
                      })}
                    </span>
                    <div className="flex-grow border-t border-gray-200"></div>
                  </div>
                )}

                {/* Show name and time above inbound messages */}
                {msg.direction === 'inbound' && shouldShowTime && (
                  <div className="flex items-center mb-1 ml-10 mt-4">
                    <span className="text-xs text-gray-600 font-medium" 
                      style={{ color: "#"+getAvatarColors(getFrom(msg)).text }}>{getDisplayName(getFrom(msg))}</span>
                    <span className="text-xs text-gray-400 ml-2">{msg.time}</span>
                  </div>
                )}

                {/* Show time above outbound messages */}
                {msg.direction === 'outbound' && shouldShowTime && (
                  <div className="flex items-center justify-end mb-1 mt-4">
                    <span className="text-xs text-gray-600 font-medium mr-2">{msg.from_num}</span>
                    <span className="text-xs text-gray-400">{msg.time}</span>
                  </div>
                )}

                <div className={`flex ${
                  msg.direction === 'outbound' 
                    ? 'justify-end' 
                    : msg.direction === 'system' 
                      ? 'justify-center' 
                      : 'justify-start'
                }`}>
                  {/* Avatar for inbound messages */}
                  {shouldShowAvatar && (
                    <div className="flex-shrink-0 mr-2">
                      <img
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(getAvatarDisplayName(getFrom(msg)))}&background=${getAvatarColors(getFrom(msg)).bg}&color=${getAvatarColors(getFrom(msg)).text}`}
                        alt={getAvatarDisplayName(getFrom(msg))}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    </div>
                  )}

                  {/* Placeholder for avatar spacing */}
                  {msg.direction === 'inbound' && !shouldShowAvatar && (
                    <div className="w-[32px] mr-2" />
                  )}

                  <div 
                    style={(() => {
                      const styles = localStorage.getItem("messaging_styles");
                      let messageStyles = {};
                      
                      if (styles) {
                        try {
                          const parsedStyles = JSON.parse(styles);
                          if (msg.direction === 'outbound') {
                            messageStyles = {
                              backgroundColor: parsedStyles.message_outbound_color || '#3B82F6',
                              color: parsedStyles.message_outbound_font_color || '#FFFFFF'
                            };
                          } else if (msg.direction === 'inbound') {
                            messageStyles = {
                              backgroundColor: parsedStyles.message_inbound_color || '#FFFFFF',
                              color: parsedStyles.message_inbound_font_color || '#1F2937'
                            };
                          }
                        } catch (e) {
                          console.error('Error parsing messaging styles:', e);
                        }
                      }
                      return messageStyles;
                    })()}
                    className={`
                    max-w-[70%] px-4 py-2 
                    ${msg.direction === 'outbound' 
                      ? (!localStorage.getItem("messaging_styles") ? 'bg-blue-500 text-white' : '')
                      : msg.direction === 'system'
                        ? 'bg-gray-200 text-gray-600 rounded-full text-sm'
                        : (!localStorage.getItem("messaging_styles") ? 'bg-white text-gray-800 shadow-sm' : 'shadow-sm')
                    }
                    ${getMessageStyle(msg, globalIndex, messages)}
                  `}>
                    {/* Media Preview */}
                    {msg.remotepath && (
                      <div className="mb-2">
                        {msg.media_type?.startsWith('image/') && (
                          <div
                            className="relative group w-full overflow-hidden rounded-lg"
                            style={!isMediaLayoutReady(msg) ? { minHeight: MMS_MIN_HEIGHT } : undefined}
                          >
                            {!isMediaLayoutReady(msg) && <MediaSkeleton height={MMS_MIN_HEIGHT} />}
                            <img 
                              ref={(element) => handleMediaRef(msg, element)}
                              src={msg.remotepath} 
                              alt="Media" 
                              className={`max-w-full rounded-lg cursor-pointer block transition-opacity duration-300 ${
                                !isMediaLayoutReady(msg) ? 'absolute inset-0 m-auto opacity-0 pointer-events-none' : 'opacity-100'
                              }`}
                              style={{ maxHeight: MMS_MIN_HEIGHT, objectFit: 'contain' }}
                              onClick={() => handleMediaClick(msg)}
                              onLoad={() => handleMediaLoad(msg)}
                              onError={() => handleMediaLoad(msg)}
                            />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(msg.remotepath, `image.${msg.media_type.split('/')[1]}`);
                              }}
                              className="absolute bottom-2 right-2 bg-white rounded-full p-1.5 shadow-lg 
                                       opacity-0 group-hover:opacity-100 transition-opacity duration-200 
                                       hover:bg-gray-100"
                            >
                              <svg className="w-4 h-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                              </svg>
                            </button>
                          </div>
                        )}
                        {msg.media_type?.startsWith('video/') && (
                          <div
                            className="relative w-full overflow-hidden rounded-lg"
                            style={!isMediaLayoutReady(msg) ? { minHeight: MMS_MIN_HEIGHT } : undefined}
                          >
                            {!isMediaLayoutReady(msg) && <MediaSkeleton height={MMS_MIN_HEIGHT} mediaType="video" />}
                          <video 
                            ref={(element) => handleMediaRef(msg, element)}
                            src={msg.remotepath} 
                            controls 
                            className={`max-w-full rounded-lg cursor-pointer block transition-opacity duration-300 ${
                              !isMediaLayoutReady(msg) ? 'absolute inset-0 m-auto opacity-0 pointer-events-none' : 'opacity-100'
                            }`}
                            style={{ maxHeight: MMS_MIN_HEIGHT }}
                            onClick={() => handleMediaClick(msg)}
                            onLoadedMetadata={() => handleMediaLoad(msg)}
                            onError={() => handleMediaLoad(msg)}
                          />
                          </div>
                        )}
                        {msg.media_type?.startsWith('audio/') && (
                          <div className="bg-gray-100 rounded-lg p-2">
                            <audio 
                              src={msg.remotepath} 
                              controls 
                              className="lg:w-[300px] w-[200px] h-8"
                              controlsList="nodownload"
                            />
                          </div>
                        )}
                        {msg.media_type?.startsWith('application/') && (
                          <div className="flex items-center p-2 bg-gray-100 rounded-lg">
                            <FontAwesomeIcon icon={faFile} className="w-6 h-6 mr-2 text-gray-600" />
                            <a 
                              href={msg.remotepath} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-sm text-gray-700 hover:underline"
                            >
                              Download File
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                    {
                      msg.text && msg.text !== " " && (
                        <p className="whitespace-pre-wrap mb-1 break-words overflow-hidden">
                          {convertUrlsToLinks(msg.text, msg.direction === 'outbound')}
                        </p>
                      )
                    }
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>

      {/* Updated Message Input */}
      <div className="flex-shrink-0 p-4 bg-white border-t border-gray-200">
        <div className="flex items-center space-x-2">
          {/* Media Attachment Button */}
          <div className="relative">
            <button
              onClick={() => setShowMediaOptions(!showMediaOptions)}
              style={(() => {
                const styles = localStorage.getItem("messaging_styles");
                let buttonStyles = {};
                
                if (styles) {
                  try {
                    const parsedStyles = JSON.parse(styles);
                    buttonStyles = {
                      backgroundColor: parsedStyles.message_buttons_color || '#3B82F6',
                      color: parsedStyles.message_buttons_font_color || '#FFFFFF'
                    };
                  } catch (e) {
                    console.error('Error parsing messaging styles:', e);
                  }
                }
                return buttonStyles;
              })()}
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors duration-200
                ${!localStorage.getItem("messaging_styles") ? 'bg-blue-500 text-white hover:bg-blue-600' : 
                'hover:opacity-80'}`}
            >
              <FontAwesomeIcon icon={faPaperclip} className="w-5 h-5" />
            </button>

            {/* Media Options Popup */}
            {showMediaOptions && (
              <div className="absolute bottom-12 left-0 bg-white rounded-lg shadow-lg p-2 w-48">
                {/* Photo/Video Input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*,video/*"
                  className="hidden"
                  id="photo-video-input"
                />
                {/* Document Input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                  className="hidden"
                  id="document-input"
                />
                {/* Audio Input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="audio/*"
                  className="hidden"
                  id="audio-input"
                />

                <button
                  onClick={() => document.getElementById('photo-video-input').click()}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center"
                >
                  <FontAwesomeIcon icon={faImage} className="w-4 h-4 mr-2" />
                  Photo/Video
                </button>
                <button
                  onClick={() => document.getElementById('document-input').click()}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center"
                >
                  <FontAwesomeIcon icon={faFile} className="w-4 h-4 mr-2" />
                  Document
                </button>
                <button
                  onClick={() => document.getElementById('audio-input').click()}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center"
                >
                  <FontAwesomeIcon icon={faMicrophone} className="w-4 h-4 mr-2" />
                  Audio
                </button>
              </div>
            )}
          </div>

          <div className="flex-1 relative">
            {/* File Preview */}
            {filePreview && (
              <div className="absolute bottom-full left-0 mb-2 p-2 bg-white rounded-lg shadow-lg max-w-[300px]">
                {fileType === 'image' && (
                  <img src={filePreview} alt="Preview" className="max-h-[200px] rounded-lg" />
                )}
                {fileType === 'video' && (
                  <video src={filePreview} controls className="max-h-[200px] rounded-lg" />
                )}
                {fileType === 'audio' && (
                  <audio src={filePreview} controls className="w-[280px]" />
                )}
                {fileType === 'application' && (
                  <div className="flex items-center p-2 bg-gray-100 rounded-lg">
                    <FontAwesomeIcon icon={faFile} className="w-6 h-6 mr-2 text-gray-600" />
                    <span className="text-sm text-gray-700 truncate">{selectedFile.name}</span>
                  </div>
                )}
                <button
                  onClick={clearFilePreview}
                  className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-gray-800 bg-opacity-50 text-white rounded-full hover:bg-opacity-75"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            <div className="relative flex-1">
              <textarea
                ref={textareaRef}
                value={message}
                onChange={handleMessageChange}
                onClick={handleTextareaClick}
                onKeyUp={handleTextareaKeyUp}
                onKeyDown={handleTextareaKeyDown}
                placeholder={filePreview ? "Add a caption..." : "Type a message..."}
                className={`
                  w-full min-h-[48px] max-h-[120px] px-4 py-2 bg-gray-100 text-base pr-12
                  ${isMultiLine ? 'rounded-xl' : 'rounded-full'}
                  resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 
                  overflow-y-auto transition-all duration-200
                `}
                rows={1}
              />
              
              {/* Emoji Button */}
              <div className="absolute right-0 inset-y-0 flex items-center pr-2 pointer-events-none">
                <button
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="text-gray-500 hover:text-gray-700 pointer-events-auto mb-1"
                >
                  <FontAwesomeIcon 
                    icon={showEmojiPicker ? faTimes : faSmile} 
                    style={{ fontSize: '24px' }} 
                  />
                </button>
              </div>

              {/* Emoji Picker */}
              {showEmojiPicker && (
                <div className="absolute bottom-full right-0 mb-2">
                  <div className="bg-white rounded-lg shadow-lg">
                    <EmojiPicker
                      onEmojiClick={handleEmojiClick}
                      width={300}
                      height={400}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleSendMessage}
            style={(() => {
              const styles = localStorage.getItem("messaging_styles");
              let buttonStyles = {};
              
              if (styles) {
                try {
                  const parsedStyles = JSON.parse(styles);
                  buttonStyles = {
                    backgroundColor: parsedStyles.message_buttons_color || '#3B82F6',
                    color: parsedStyles.message_buttons_font_color || '#FFFFFF'
                  };
                } catch (e) {
                  console.error('Error parsing messaging styles:', e);
                }
              }
              return buttonStyles;
            })()}
            className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors duration-200
              ${!localStorage.getItem("messaging_styles") ? 'bg-blue-500 text-white hover:bg-blue-600' : 
              'hover:opacity-80'}`}
          >
            {sending ? (
              <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Media Preview Modal */}
      <MediaPreviewModal
        media={previewMedia}
        isOpen={showPreview}
        onClose={() => {
          setShowPreview(false);
          setPreviewMedia(null);
        }}
      />
    </div>
  );
}

export default Messages;
